// Companies > detail > Payouts: the operator's Paystack subaccount and split
// per env (Paystack plan 6.6 and 6.7, PR 7a). "Create live subaccount",
// "Verified in Paystack" and clearing a create that got no answer are super
// admin actions (the server answers 403 to anyone else). Since PR 7b (#541) a
// block may wait for a bank change to reach Paystack.

import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import { AdminCompanyAPI } from '../../services/api';
import { formatDateTime, ENV_LABEL, BLOCK_LABEL, OUTCOME_LABEL, errorText } from './payoutLabels';

const Row = ({ label, children }) => (
  <div>
    <dt className="text-gray-500">{label}</dt>
    <dd className="mt-0.5 font-medium text-gray-900">{children}</dd>
  </div>
);

const EnvBlock = ({ env, block, children }) => (
  <div className="border border-gray-200 rounded-md p-4">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <p className="font-medium text-gray-900">{ENV_LABEL[env]}</p>
      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{BLOCK_LABEL[block.status] || block.status}</span>
    </div>
    {block.status !== 'none' && (
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
        {block.subaccountCode && <Row label="Subaccount"><span className="font-mono break-all">{block.subaccountCode}</span></Row>}
        {block.splitCode && <Row label="Split"><span className="font-mono break-all">{block.splitCode}</span>{block.splitId ? ` (id ${block.splitId})` : ''}</Row>}
        {block.percentageCharge != null && <Row label="Fallback shares (no charge uses them)">Zuttle {block.percentageCharge}%, operator {100 - block.percentageCharge}%</Row>}
        {block.createdAt && <Row label="Created">{formatDateTime(block.createdAt)}</Row>}
        {block.verifiedAt && <Row label="Verified">{formatDateTime(block.verifiedAt)}</Row>}
        {block.updatedAt && <Row label="Account changed at Paystack">{formatDateTime(block.updatedAt)}</Row>}
        {block.validation && <Row label="Live bank check">{OUTCOME_LABEL[block.validation.outcome] || block.validation.outcome}{block.validation.message ? `: ${block.validation.message}` : ''}</Row>}
      </dl>
    )}
    {block.pendingUpdate && (
      <p className="text-sm text-amber-700 mb-3">
        Paystack still pays the old account here: the bank change has not reached it.
        {block.notSentReason ? ` Not sent from this server: ${block.notSentReason}.` : ''}
      </p>
    )}
    {block.lastError && <p className="text-sm text-amber-700 mb-3">Last error: {block.lastError}</p>}
    {block.pendingUpdate && block.lastError && (
      <p className="text-sm text-gray-600 mb-3">
        If this keeps failing, Paystack may not accept the new account's bank. The operator can save the account again.
      </p>
    )}
    {block.clearNote && block.status !== 'manual_review' && <p className="text-xs text-gray-500 mb-3">Cleared {formatDateTime(block.clearedAt)}: {block.clearNote}</p>}
    {children}
  </div>
);

const PayoutSubaccounts = ({ companyId, data, isSuperAdmin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState({});
  const [acceptLetter, setAcceptLetter] = useState(false);
  const { subaccounts, server, review, letter } = data;
  if (!subaccounts || !review) return null;

  const run = async (call) => {
    setBusy(true);
    setError(null);
    try {
      const res = await call();
      onChanged(res.data.data);
      setNotes({});
      setAcceptLetter(false);
    } catch (err) {
      setError(errorText(err, 'That did not go through'));
      await onChanged(null);
    } finally {
      setBusy(false);
    }
  };

  // A made-up block is not at Paystack, so nothing is asked there (#528).
  const verify = (env) => {
    if (!window.confirm(env === 'simulation'
      ? 'Mark this made-up Simulation subaccount as verified? It does not exist at Paystack, so Zuttle does not ask Paystack to confirm it.'
      : `Did you verify this ${ENV_LABEL[env]} subaccount on the Paystack Dashboard? Zuttle asks Paystack to confirm it.`)) return;
    run(() => AdminCompanyAPI.markSubaccountVerified(companyId, env));
  };
  // A bank change Paystack has not taken: the approve route sends what is missing (#541).
  const resend = () => run(() => AdminCompanyAPI.approveBankDetails(companyId));
  const clear = (env) => run(() => AdminCompanyAPI.clearUnknownCreate(companyId, env, notes[env] || ''));
  const createLive = () => {
    if (!window.confirm('Run a live bank check (R3) and create the live subaccount and split at Paystack?')) return;
    run(() => AdminCompanyAPI.createLiveSubaccount(companyId, acceptLetter));
  };

  // Simulation shows only where it is used; test where approval uses it (`paystack_test`) or a block exists; live always.
  const shown = ['simulation', 'test', 'live'].filter((env) => env === 'live'
    || subaccounts[env].status !== 'none'
    || server.approvalEnv === env);
  const live = subaccounts.live;
  const canCreateLive = review.status === 'approved'
    && (live.status === 'none' || (live.status === 'manual_review' && live.reason === 'check_failed'));

  const actions = (env) => {
    const block = subaccounts[env];
    if (!isSuperAdmin) return null;
    if (block.pendingUpdate) {
      return block.notSentReason ? null : (
        <button type="button" disabled={busy || review.status !== 'approved'} onClick={resend} className="px-3 py-2 border border-emerald-600 text-emerald-700 text-sm rounded-md hover:bg-emerald-50 disabled:opacity-50">
          Send the change to Paystack again
        </button>
      );
    }
    if (block.status === 'created') {
      return (
        <button type="button" disabled={busy} onClick={() => verify(env)} className="px-3 py-2 border border-emerald-600 text-emerald-700 text-sm rounded-md hover:bg-emerald-50 disabled:opacity-50">
          Verified in Paystack
        </button>
      );
    }
    if (block.status === 'manual_review' && block.reason === 'create_unknown') {
      return (
        <div className="space-y-2">
          <p className="text-sm text-gray-700">
            The last create got no answer, so it is never sent again by itself. Look for this company on the Paystack Dashboard and deactivate any subaccount or split the create made. Then clear it, and the next try creates what is missing.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <input
              aria-label={`What the dashboard shows (${env})`}
              placeholder="What the dashboard shows"
              value={notes[env] || ''}
              onChange={(e) => setNotes({ ...notes, [env]: e.target.value })}
              className="w-80 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <button type="button" disabled={busy || (notes[env] || '').trim().length < 10} onClick={() => clear(env)} className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50">
              Clear after the dashboard check
            </button>
          </div>
        </div>
      );
    }
    if (env === 'live' && canCreateLive) {
      return (
        <div className="space-y-2">
          {!server.liveKey && <p className="text-sm text-gray-500">The Paystack live key is not set on this server.</p>}
          {live.status === 'manual_review' && <p className="text-sm text-amber-700">The live bank check did not pass. Create it only with a bank letter you accepted.</p>}
          {letter?.status === 'pending' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={acceptLetter} onChange={(e) => setAcceptLetter(e.target.checked)} />
              I checked the bank letter: accept it
            </label>
          )}
          <button type="button" disabled={busy || !server.liveKey} onClick={createLive} className="px-3 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50">
            Create live subaccount
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mt-6 border-t border-gray-100 pt-4">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={16} className="text-emerald-600" />
        <h3 className="text-sm font-semibold text-gray-700">Paystack subaccounts</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Payment mode: {server.mode}. Test key: {server.testKey ? 'set' : 'not set'}. Live key: {server.liveKey ? 'set' : 'not set'}.
        {' '}{server.checkEnv ? `The bank check at save runs in: ${ENV_LABEL[server.checkEnv]}${server.mode === 'paystack_test' && !server.testKey ? ' (it cannot run: the test key is not set)' : ''}.` : 'The bank check at save does not run here.'}
        {' '}{server.approvalBlocked ? 'Approval is blocked: the Paystack test key is not set.' : server.approvalEnv ? `Approval creates the subaccount in: ${ENV_LABEL[server.approvalEnv]}.` : 'Approval creates no subaccount here.'}
        {' '}A live one only comes from &quot;Create live subaccount&quot;.
        {server.mode === 'simulation' && ' In simulation the bank check and the subaccount codes are made up, even with the test key set: no operator details go to Paystack test mode.'}
      </p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {shown.map((env) => (
          <EnvBlock key={env} env={env} block={subaccounts[env]}>{actions(env)}</EnvBlock>
        ))}
      </div>
    </div>
  );
};

export default PayoutSubaccounts;
